import { useState } from "react";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/use-toast";

/**
 * Modal bloquant qui force la ré-acceptation des CGU lorsque l'utilisateur
 * n'a pas encore accepté la version en cours (`current_cgu_version` != `cgu_accepted_version`).
 * Ne rend rien si l'utilisateur est à jour ou non connecté.
 */
export default function CGUReAcceptModal() {
  const { user, setUser } = useAuth();
  const { toast } = useToast();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!user || user.cgu_up_to_date !== false) return null;

  const handleAccept = async () => {
    if (!checked) {
      toast({ variant: "destructive", title: "Merci de cocher la case pour continuer" });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/accept-cgu");
      // Reload the fresh user with cgu_up_to_date=true
      const res = await api.get("/auth/me");
      if (res?.data) {
        setUser(res.data);
        localStorage.setItem("user", JSON.stringify(res.data));
      }
      toast({ title: "Merci ! Les nouvelles CGU ont bien été acceptées." });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Réessaie dans un instant." });
    } finally {
      setSubmitting(false);
    }
  };

  const version = user.current_cgu_version || "1.1";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: "rgba(15, 37, 64, 0.72)", backdropFilter: "blur(6px)" }}
      data-testid="cgu-reaccept-modal"
    >
      <div className="glass-card rounded-2xl max-w-lg w-full p-8 shadow-2xl" style={{ background: "#FAF7F1" }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "#E5D4A8" }}>
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="#0F2540">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: "Fraunces, serif", color: "#0F2540" }}>
              Nouvelles Conditions Générales d&apos;Utilisation
            </h2>
            <p className="text-xs uppercase tracking-widest mt-1" style={{ color: "#C9A86A", letterSpacing: "2px" }}>
              Version {version}
            </p>
          </div>
        </div>

        <p className="text-sm mb-4" style={{ color: "#1A2A3F", lineHeight: "1.7" }}>
          Nous avons mis à jour nos CGU pour intégrer notamment :
        </p>
        <ul className="text-sm space-y-2 mb-5" style={{ color: "#1A2A3F" }}>
          <li className="flex gap-2"><span style={{ color: "#C9A86A" }}>◆</span> l&apos;information au titre de l&apos;<strong>Article 50 du Règlement (UE) 2024/1689 « AI Act »</strong> sur les résumés, transcriptions et traductions générés par IA</li>
          <li className="flex gap-2"><span style={{ color: "#C9A86A" }}>◆</span> la nouvelle option d&apos;authentification via <strong>Google Sign-In</strong></li>
          <li className="flex gap-2"><span style={{ color: "#C9A86A" }}>◆</span> la liste actualisée de nos sous-traitants (Fathom, Emergent, Google, Anthropic, OpenAI)</li>
        </ul>

        <a
          href="/cgu.html"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium inline-flex items-center gap-1 mb-5"
          style={{ color: "#0F2540", textDecoration: "underline", textDecorationColor: "#C9A86A", textUnderlineOffset: "3px" }}
          data-testid="cgu-reaccept-open-link"
        >
          Lire la version intégrale des CGU
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
        </a>

        <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border" style={{ borderColor: checked ? "#C9A86A" : "rgba(15, 37, 64, 0.12)", background: checked ? "#F4EFE3" : "white" }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-1 w-4 h-4"
            style={{ accentColor: "#C9A86A" }}
            data-testid="cgu-reaccept-checkbox"
          />
          <span className="text-sm" style={{ color: "#1A2A3F", lineHeight: "1.6" }}>
            J&apos;accepte la nouvelle version des Conditions Générales d&apos;Utilisation (v{version}) d&apos;Odys&apos;Mentoring.
          </span>
        </label>

        <button
          onClick={handleAccept}
          disabled={!checked || submitting}
          className="w-full mt-5 py-3 rounded-lg font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: "#0F2540",
            color: "#FAF7F1",
            letterSpacing: "1px",
            textTransform: "uppercase",
          }}
          onMouseOver={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = "#1E3A5F"; }}
          onMouseOut={(e) => { e.currentTarget.style.background = "#0F2540"; }}
          data-testid="cgu-reaccept-submit-btn"
        >
          {submitting ? "Enregistrement..." : "Continuer vers l'application"}
        </button>

        <p className="text-xs text-center mt-4" style={{ color: "#5A6A7F" }}>
          Sans acceptation, l&apos;accès à l&apos;Application reste bloqué.
        </p>
      </div>
    </div>
  );
}
